// Phase 2D-fix — Pure helper that decides where the user should land
// after logout. Extracted from AuthContext.logout() so it can be unit-
// tested in isolation without mocking React, sessionStorage, axios or
// window.location.
//
// Slug resolution priority (strict order):
//   A. sessionStorage["sdrive_tenant_slug"]
//      Written by CompanyRedirect (/:slug/{booking|client|driver|login}),
//      CompanyLanding (/:slug) and LoginPage (?company=...). Covers the
//      vast majority of real entry flows.
//   B. user payload — defensive read of `company_slug` / `tenant_slug`
//      / `slug`. NONE of these fields are currently emitted by the
//      backend (server.py /auth/login returns the raw user document
//      which only contains `company_id` UUID — NOT a URL slug). The
//      read is kept so that any future backend addition transparently
//      activates the fallback without another frontend patch.
//
// We never invent a slug from `company_id`: the public tenant routes
// (/:slug) resolve via `db.companies.slug`, not via `company_id`, so a
// UUID would simply 404 once the page loads.
//
// All produced destinations are pathnames (leading "/"). The caller is
// expected to feed them to window.location.assign(...) so the
// navigation can never be overwritten by an in-flight React Router
// <Navigate to="/"> from <ProtectedRoute>.

// Validate a tenant slug for use in /:slug URLs.
//   - must be a non-empty trimmed string
//   - must not be the literal strings "undefined" / "null"
//   - must not contain "/" or any URL separator
//   - must match the same charset accepted by the backend
//     (`/^[a-z0-9][a-z0-9\-]{0,62}$/i`, see CompanyRedirect.jsx)
export function isValidTenantSlug(raw) {
  if (typeof raw !== "string") return false;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return false;
  if (trimmed === "undefined" || trimmed === "null") return false;
  if (trimmed.includes("/")) return false;
  return /^[a-z0-9][a-z0-9\-]{0,62}$/i.test(trimmed);
}

// Resolve the post-logout destination.
//
// Inputs:
//   - role:        the user's role at the moment they clicked logout
//                  (snapshotted BEFORE setUser(null)).
//   - sessionSlug: the raw value of sessionStorage["sdrive_tenant_slug"]
//                  at the moment of logout (may be null/empty/garbage).
//   - user:        the user object at the moment of logout (snapshotted
//                  BEFORE setUser(null)). May be null.
//
// Output: a pathname string, always safe to pass to
// window.location.assign().
export function resolveLogoutDestination({ role, sessionSlug, user }) {
  // Super-admins never land in a tenant.
  if (role === "super_admin") return "/";

  // A. sessionStorage
  let rawSlug = isValidTenantSlug(sessionSlug) ? sessionSlug.trim() : null;

  // B. user payload (defensive — see file header)
  if (!rawSlug && user && typeof user === "object") {
    const fromUser =
      user.company_slug || user.tenant_slug || user.slug || null;
    if (isValidTenantSlug(fromUser)) rawSlug = fromUser.trim();
  }

  if (!rawSlug) return "/";

  const sub = role === "driver" ? "/driver" : "";
  return `/${encodeURIComponent(rawSlug)}${sub}`;
}
